/**
 * POS (point-of-sale) procedures — the retail counter contract.
 *
 * Wire schemas for `pos.ringSale`. A POS sale is NOT a new entity: the domain
 * composes the existing `issueInvoice` + `recordReceipt` seams in one tx, so a
 * sale comes out as a PAID invoice plus N receipts. This contract only carries
 * the counter's input shape (lines priced at tax-INCLUSIVE retail MRP) and the
 * thin result the SPA needs.
 *
 * @see docs/product/pos-implementation-plan.md §3.3 (ringSale)
 */
import { z } from "zod";
import { moneyPaise, uuid, isoDate, cursor, paged } from "../_common.js";
import { receiptMode } from "./receipts.js";

/** A counter line. `mrpPaise` is the tax-INCLUSIVE retail price per unit; the
 *  domain back-computes the tax-exclusive rate before issuing the invoice. */
export const ringSaleLine = z.object({
  itemId: uuid.nullable().optional(),
  description: z.string().min(1).max(500),
  hsn: z.string().min(4).max(8),
  unit: z.string().min(1).max(16).default("Nos"),
  qtyMilli: z.number().int().positive(),
  mrpPaise: moneyPaise,
  taxRateBps: z.number().int().min(0).max(10_000),
  discountBps: z.number().int().min(0).max(10_000).default(0),
});

/** One tender. The skeleton takes a single tender; Phase 1 allows several
 *  (split cash/card/UPI) that must sum to the grand total. */
export const ringSaleTender = z.object({
  mode: receiptMode,
  amountPaise: moneyPaise,
  reference: z.string().trim().max(64).optional(),
});

export const ringSaleInput = z.object({
  /** A named buyer chosen at the counter. Omit for an anonymous counter sale —
   *  the domain falls back to a phone-upserted walk-in or the seeded
   *  "Counter Sales" default. @see docs/spec/04-pos.md AC10 */
  customerId: uuid.optional(),
  /** Optional buyer phone — upserts a lightweight walk-in when no customerId. */
  walkInPhone: z.string().trim().min(10).max(16).optional(),
  /** 2-digit GST state code of the place of supply. Omit at the counter — it
   *  defaults to the company's own state (a counter sale is intra-state). */
  placeOfSupply: z.string().regex(/^\d{2}$/, "2-digit state code required").optional(),
  issuedOn: isoDate,
  dueOn: isoDate,
  lines: z.array(ringSaleLine).min(1),
  tenders: z.array(ringSaleTender).min(1),
});

export const ringSaleOutput = z.object({
  invoiceId: uuid,
  docNumber: z.string(),
  status: z.literal("paid"),
  /** Cash returned to the customer on overpay. Always "0" in the skeleton
   *  (strict tender == total); Phase 1 relaxes this for cash overpay. */
  changePaise: moneyPaise,
});

export type RingSaleInput = z.infer<typeof ringSaleInput>;
export type RingSaleOutput = z.infer<typeof ringSaleOutput>;

/** Quote the GST-correct total for a cart before charging (pure, read-only). */
export const quoteInput = z.object({
  lines: z.array(ringSaleLine).min(1),
  placeOfSupply: z.string().regex(/^\d{2}$/).optional(),
});

export const quoteOutput = z.object({
  subtotalPaise: moneyPaise,
  cgstPaise: moneyPaise,
  sgstPaise: moneyPaise,
  igstPaise: moneyPaise,
  roundOffPaise: z.string(),
  grandTotalPaise: moneyPaise,
  taxMode: z.enum(["intra", "inter"]),
});

export type QuoteInput = z.infer<typeof quoteInput>;
export type QuoteOutput = z.infer<typeof quoteOutput>;

// ── Cash-drawer shifts (Phase 2) ─────────────────────────────────────
export const openShiftInput = z.object({
  /** Defaults to the company's "Register 1" when omitted. */
  registerId: uuid.optional(),
  /** Counted cash in the drawer at open. */
  openingFloatPaise: moneyPaise,
});

export const openShiftOutput = z.object({
  shiftId: uuid,
  registerId: uuid,
  registerName: z.string(),
  openingFloatPaise: moneyPaise,
  openedAt: z.string(),
});

export const closeShiftInput = z.object({
  shiftId: uuid,
  countedCashPaise: moneyPaise,
  /** Required when the drawer doesn't balance. */
  varianceReason: z.string().trim().max(500).optional(),
});

export const closeShiftOutput = z.object({
  shiftId: uuid,
  status: z.literal("closed"),
  openingFloatPaise: moneyPaise,
  cashSalesPaise: moneyPaise,
  expectedCashPaise: moneyPaise,
  countedCashPaise: moneyPaise,
  /** counted − expected; may be negative (short) — hence a plain string. */
  variancePaise: z.string(),
});

export const currentShiftOutput = z.object({
  shift: z
    .object({
      shiftId: uuid,
      registerId: uuid,
      openingFloatPaise: moneyPaise,
      openedAt: z.string(),
    })
    .nullable(),
});

// ── Returns / refunds (Phase 3) ──────────────────────────────────────
export const returnSaleInput = z.object({
  /** The original paid POS invoice being returned (full reversal in V1). */
  invoiceId: uuid,
  reason: z.string().trim().min(3).max(500),
  returnedOn: isoDate.optional(),
});

export const returnSaleOutput = z.object({
  creditNoteId: uuid,
  creditNoteDocNumber: z.string(),
  refundPaise: moneyPaise,
  invoiceReversed: z.boolean(),
  refundAwaitingApproval: uuid.nullable(),
});

export const listShiftsInput = z.object({
  cursor: cursor,
  limit: z.number().int().min(1).max(100).default(30),
});

export const shiftListRow = z.object({
  id: uuid,
  registerName: z.string(),
  cashierName: z.string(),
  status: z.enum(["open", "closed"]),
  openingFloatPaise: moneyPaise,
  expectedCashPaise: moneyPaise.nullable(),
  countedCashPaise: moneyPaise.nullable(),
  variancePaise: z.string().nullable(),
  varianceReason: z.string().nullable(),
  openedAt: z.string(),
  closedAt: z.string().nullable(),
});

export const listShiftsOutput = paged(shiftListRow);

export const shiftSummaryInput = z.object({ shiftId: uuid });

export const shiftSummaryOutput = z.object({
  shiftId: uuid,
  status: z.enum(["open", "closed"]),
  openedAt: z.string(),
  openingFloatPaise: moneyPaise,
  cashPaise: moneyPaise,
  cardPaise: moneyPaise,
  upiPaise: moneyPaise,
  otherPaise: moneyPaise,
  grossSalesPaise: moneyPaise,
  expectedCashPaise: moneyPaise,
  salesCount: z.number().int(),
  variancePaise: z.string().nullable(),
});
